/**
 * Task Manager Service for handling persistent generation tasks
 */

const STORAGE_KEY = 'records_generation_tasks';
export const TASKS_UPDATED_EVENT = 'cosigned-tasks-updated';
export const TASK_RESULT_EVENT = 'cosigned-task-result';

export const TASK_TYPES = {
  HTML: 'html',
  PDF: 'pdf',
  SURVEY_EXPORT: 'survey_export',
  MRR_EXPORT: 'mrr_export',
  NOTES_EXPORT: 'notes_export',
  CLINICAL_TAG_EVAL: 'clinical_tag_eval',
  LIGHTHOUSE_INVITE: 'lighthouse_invite',
};

const TASK_TYPE_DEFAULTS = {
  [TASK_TYPES.HTML]: {
    displayName: 'HTML Records',
    startedMessage: 'HTML generation started',
    actionLabel: 'View',
    resultMode: 'open',
    autoRunOnSuccess: false,
  },
  [TASK_TYPES.PDF]: {
    displayName: 'PDF Records',
    startedMessage: 'PDF generation started',
    actionLabel: 'Download',
    resultMode: 'download',
    autoRunOnSuccess: false,
  },
  [TASK_TYPES.SURVEY_EXPORT]: {
    displayName: 'Survey CSV Export',
    startedMessage: 'Survey export queued',
    actionLabel: 'Download again',
    resultMode: 'download',
    autoRunOnSuccess: true,
  },
  [TASK_TYPES.MRR_EXPORT]: {
    displayName: 'MRR Cohort CSV Export',
    startedMessage: 'MRR cohort export queued',
    actionLabel: 'Download again',
    resultMode: 'download',
    autoRunOnSuccess: true,
  },
  [TASK_TYPES.NOTES_EXPORT]: {
    displayName: 'Updates CSV Export',
    startedMessage: 'Updates export queued',
    actionLabel: 'Download again',
    resultMode: 'download',
    autoRunOnSuccess: true,
  },
  [TASK_TYPES.CLINICAL_TAG_EVAL]: {
    displayName: 'Clinical Tag Evaluation',
    startedMessage: 'Clinical tag evaluation queued',
    actionLabel: 'View result',
    resultMode: 'json',
    autoRunOnSuccess: true,
  },
  [TASK_TYPES.LIGHTHOUSE_INVITE]: {
    displayName: 'Lighthouse Invite',
    startedMessage: 'Invite send queued',
    actionLabel: 'Dismiss',
    resultMode: 'json',
    autoRunOnSuccess: true,
  },
};

const emitTasksUpdated = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(TASKS_UPDATED_EVENT));
  }
};

export const emitTaskResult = (task, result) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TASK_RESULT_EVENT, {
      detail: { task, result },
    }));
  }
};

export const normalizeTaskStatus = (status) => {
  if (status === 'failure' || status === 'error') return 'failed';
  if (status === 'completed') return 'success';
  return status || 'pending';
};

export const isTaskInProgress = (task) => (
  ['started', 'pending', 'progress', 'queued'].includes(normalizeTaskStatus(task.status))
);

export const isTaskCompleted = (task) => normalizeTaskStatus(task.status) === 'success';

export const isTaskFailed = (task) => normalizeTaskStatus(task.status) === 'failed';

export const getTaskTypeDefaults = (type) => TASK_TYPE_DEFAULTS[type] || {
  displayName: 'Background Task',
  startedMessage: 'Task started',
  actionLabel: 'Open',
  resultMode: 'json',
  autoRunOnSuccess: false,
};

/**
 * Get all stored tasks
 */
export const getStoredTasks = () => {
  try {
    const tasks = localStorage.getItem(STORAGE_KEY);
    return tasks ? JSON.parse(tasks) : [];
  } catch (error) {
    console.error('Error reading stored tasks:', error);
    return [];
  }
};

/**
 * Store a new task
 */
export const storeTask = (task) => {
  try {
    const tasks = getStoredTasks();
    const newTask = {
      ...task,
      status: normalizeTaskStatus(task.status),
      createdAt: new Date().toISOString(),
      lastChecked: new Date().toISOString()
    };
    
    const existingIndex = tasks.findIndex(existing => existing.taskId === newTask.taskId);
    if (existingIndex >= 0) {
      tasks[existingIndex] = { ...tasks[existingIndex], ...newTask };
    } else {
      tasks.push(newTask);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
    emitTasksUpdated();
    return newTask;
  } catch (error) {
    console.error('Error storing task:', error);
    return task;
  }
};

/**
 * Update an existing task
 */
export const updateTask = (taskId, updates) => {
  try {
    const tasks = getStoredTasks();
    const taskIndex = tasks.findIndex(task => task.taskId === taskId);
    
    if (taskIndex !== -1) {
      tasks[taskIndex] = {
        ...tasks[taskIndex],
        ...updates,
        status: normalizeTaskStatus(updates.status || tasks[taskIndex].status),
        lastChecked: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
      emitTasksUpdated();
      return tasks[taskIndex];
    }
    return null;
  } catch (error) {
    console.error('Error updating task:', error);
    return null;
  }
};

/**
 * Remove a task
 */
export const removeTask = (taskId) => {
  try {
    const tasks = getStoredTasks();
    const filteredTasks = tasks.filter(task => task.taskId !== taskId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredTasks));
    emitTasksUpdated();
    return true;
  } catch (error) {
    console.error('Error removing task:', error);
    return false;
  }
};

/**
 * Get tasks that are still in progress
 */
export const getInProgressTasks = () => {
  const tasks = getStoredTasks();
  return tasks.filter(isTaskInProgress);
};

/**
 * Get completed tasks
 */
export const getCompletedTasks = () => {
  const tasks = getStoredTasks();
  return tasks.filter(isTaskCompleted);
};

/**
 * Get failed tasks
 */
export const getFailedTasks = () => {
  const tasks = getStoredTasks();
  return tasks.filter(isTaskFailed);
};

/**
 * Clean up old tasks (older than 7 days)
 */
export const cleanupOldTasks = () => {
  try {
    const tasks = getStoredTasks();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const activeTasks = tasks.filter(task => {
      const taskDate = new Date(task.createdAt);
      return taskDate > sevenDaysAgo || isTaskCompleted(task); // Keep recent tasks and completed ones
    });
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(activeTasks));
    if (activeTasks.length !== tasks.length) emitTasksUpdated();
    return tasks.length - activeTasks.length; // Return number of cleaned up tasks
  } catch (error) {
    console.error('Error cleaning up tasks:', error);
    return 0;
  }
};

/**
 * Create a task object
 */
export const createTask = (taskId, type, userIdOrOptions = null, recordTypeFilter = 'all') => {
  const defaults = getTaskTypeDefaults(type);
  const options = (
    userIdOrOptions && typeof userIdOrOptions === 'object' && !Array.isArray(userIdOrOptions)
  ) ? userIdOrOptions : { userId: userIdOrOptions, recordTypeFilter };

  return {
    taskId,
    type,
    userId: options.userId || null,
    recordTypeFilter: options.recordTypeFilter || 'all',
    status: normalizeTaskStatus(options.status || 'started'),
    message: options.message || defaults.startedMessage,
    title: options.title || defaults.displayName,
    actionLabel: options.actionLabel || defaults.actionLabel,
    resultMode: options.resultMode || defaults.resultMode,
    autoRunOnSuccess: options.autoRunOnSuccess ?? defaults.autoRunOnSuccess,
    actionStatus: options.actionStatus || 'idle',
    ...options,
    createdAt: new Date().toISOString(),
    lastChecked: new Date().toISOString()
  };
};

const getDisplayName = (task) => {
  if (task.title || task.displayName) return task.title || task.displayName;
  if (task.type === TASK_TYPES.HTML || task.type === TASK_TYPES.PDF) {
    return `${task.type.toUpperCase()} Records${task.recordTypeFilter !== 'all' ? ` (${task.recordTypeFilter})` : ''}`;
  }
  return getTaskTypeDefaults(task.type).displayName;
};

/**
 * Format task for display
 */
export const formatTaskForDisplay = (task) => {
  const createdDate = new Date(task.createdAt);
  const now = new Date();
  const diffMinutes = Math.floor((now - createdDate) / (1000 * 60));
  
  let timeAgo;
  if (diffMinutes < 1) {
    timeAgo = 'Just now';
  } else if (diffMinutes < 60) {
    timeAgo = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else {
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
  }
  
  return {
    ...task,
    status: normalizeTaskStatus(task.status),
    timeAgo,
    displayName: getDisplayName(task),
    actionLabel: task.actionLabel || getTaskTypeDefaults(task.type).actionLabel,
    resultMode: task.resultMode || getTaskTypeDefaults(task.type).resultMode,
    autoRunOnSuccess: task.autoRunOnSuccess ?? getTaskTypeDefaults(task.type).autoRunOnSuccess,
    isActionCompleted: !!task.actionCompletedAt || task.actionStatus === 'success',
    isActionRunning: task.actionStatus === 'running',
    actionError: task.actionError || null,
    isCompleted: isTaskCompleted(task),
    isFailed: isTaskFailed(task),
    isInProgress: isTaskInProgress(task)
  };
};
